import { useEffect, useState } from "react";
import { ChefHat, Edit3, Plus, Power } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { usePos } from "../../context/PosContext";
import PageHeader from "../../components/PageHeader";

export default function KitchensPage() {
  const { session, hasPerm } = useAuth();
  const { outlets, outletId } = usePos();

  const [selectedOutletId, setSelectedOutletId] = useState(outletId || "");
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const canManage = hasPerm("kitchen.manage");

  useEffect(() => {
    if (!selectedOutletId && outletId) {
      setSelectedOutletId(outletId);
    }
  }, [outletId, selectedOutletId]);

  const load = async () => {
    if (!selectedOutletId) {
      setItems([]);
      return;
    }

    setLoading(true);

    try {
      const { data } = await api.get("/kitchens", {
        params: { outlet_id: selectedOutletId },
      });
      setItems(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [selectedOutletId]);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setShow(true);
  };

  const openEdit = (kitchen) => {
    setEditing(kitchen);
    setName(kitchen.name || "");
    setShow(true);
  };

  const save = async (e) => {
    e.preventDefault();

    const trimmedName = name.trim();

    if (!trimmedName) {
      toast.error("Kitchen name is required");
      return;
    }

    try {
      if (editing) {
        await api.patch(`/kitchens/${editing.id}`, {
          name: trimmedName,
        });
        toast.success("Kitchen updated");
      } else {
        await api.post("/kitchens", {
          outlet_id: selectedOutletId,
          name: trimmedName,
        });
        toast.success("Kitchen created");
      }

      setShow(false);
      setEditing(null);
      setName("");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleStatus = async (kitchen) => {
    try {
      await api.patch(`/kitchens/${kitchen.id}/status`, {
        active: !kitchen.active,
      });

      toast.success(
        kitchen.active ? "Kitchen deactivated" : "Kitchen activated"
      );

      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const selectedOutlet = outlets.find((o) => o.id === selectedOutletId);

  return (
    <div data-testid="kitchens-page">
      <PageHeader
        title="Kitchens"
        subtitle="Manage production kitchens by outlet"
        testid="kitchens-header"
      >
        {canManage && (
          <button
            onClick={openCreate}
            disabled={!selectedOutletId}
            data-testid="kitchen-create-button"
            className="h-10 px-4 rounded-xl bg-primary text-primary-foreground text-sm font-bold flex items-center gap-2 hover:opacity-90 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={16} /> New Kitchen
          </button>
        )}
      </PageHeader>

      <div className="mb-5 max-w-md">
        <label
          htmlFor="kitchen-outlet-select"
          className="block text-xs font-semibold text-muted-foreground mb-1.5"
        >
          Outlet
        </label>

        <select
          id="kitchen-outlet-select"
          data-testid="kitchen-outlet-select"
          value={selectedOutletId}
          onChange={(e) => setSelectedOutletId(e.target.value)}
          className="w-full h-11 px-3 rounded-xl bg-card border border-border text-sm focus:border-primary focus:outline-none"
        >
          {outlets.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name} ({o.code})
            </option>
          ))}
        </select>
      </div>

      {selectedOutlet && (
        <p
          className="text-xs text-muted-foreground mb-4"
          data-testid="kitchen-selected-outlet"
        >
          Showing kitchens for <span className="font-semibold text-foreground">{selectedOutlet.name}</span>
        </p>
      )}

      {loading ? (
        <div className="bg-card border border-border rounded-2xl p-8 text-center text-sm text-muted-foreground">
          Loading kitchens…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((kitchen) => (
            <div
              key={kitchen.id}
              className="bg-card border border-border rounded-2xl p-5"
              data-testid={`kitchen-card-${kitchen.code}`}
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
                  <ChefHat size={18} />
                </div>

                <span className="font-mono text-xs text-muted-foreground">
                  {kitchen.code}
                </span>
              </div>

              <h3 className="font-heading font-semibold mt-3">
                {kitchen.name}
              </h3>

              <span
                className={`inline-block mt-3 px-2 py-0.5 rounded-md text-[11px] font-bold ${
                  kitchen.active
                    ? "bg-emerald-500/15 text-emerald-400"
                    : "bg-red-500/15 text-red-400"
                }`}
              >
                {kitchen.active ? "ACTIVE" : "INACTIVE"}
              </span>

              {canManage && (
                <div className="flex items-center gap-2 mt-4">
                  <button
                    onClick={() => openEdit(kitchen)}
                    data-testid={`kitchen-edit-${kitchen.code}`}
                    className="flex-1 h-9 rounded-lg bg-secondary text-sm font-semibold flex items-center justify-center gap-2 hover:bg-accent"
                  >
                    <Edit3 size={14} />
                    Edit
                  </button>

                  <button
                    onClick={() => toggleStatus(kitchen)}
                    data-testid={`kitchen-toggle-${kitchen.code}`}
                    className={`h-9 px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 ${
                      kitchen.active
                        ? "bg-secondary text-red-400 hover:bg-red-500/10"
                        : "bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/15"
                    }`}
                  >
                    <Power size={14} />
                    {kitchen.active ? "OFF" : "ON"}
                  </button>
                </div>
              )}
            </div>
          ))}

          {items.length === 0 && (
            <div
              className="md:col-span-2 lg:col-span-3 bg-card border border-border rounded-2xl p-8 text-center"
              data-testid="kitchens-empty"
            >
              <ChefHat
                size={28}
                className="mx-auto text-muted-foreground mb-3"
              />
              <p className="text-sm font-semibold">No kitchens yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Create a kitchen for this outlet to start routing products.
              </p>
            </div>
          )}
        </div>
      )}

      {show && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShow(false)}
        >
          <form
            onSubmit={save}
            className="bg-card border border-border w-full max-w-md rounded-2xl p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
            data-testid="kitchen-form-modal"
          >
            <div>
              <h3 className="font-heading text-lg font-bold">
                {editing ? "Edit Kitchen" : "New Kitchen"}
              </h3>

              <p className="text-xs text-muted-foreground mt-1">
                {selectedOutlet?.name || "Selected outlet"}
              </p>
            </div>

            <div>
              <label
                htmlFor="kitchen-name"
                className="block text-xs font-semibold text-muted-foreground mb-1.5"
              >
                Kitchen name
              </label>

              <input
                id="kitchen-name"
                data-testid="kitchen-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Kitchen Produksi"
                maxLength={100}
                required
                autoFocus
                className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
              />
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShow(false)}
                data-testid="kitchen-form-cancel"
                className="flex-1 h-11 rounded-xl bg-secondary text-sm font-semibold"
              >
                Cancel
              </button>

              <button
                type="submit"
                data-testid="kitchen-form-submit"
                className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm"
              >
                {editing ? "Save Changes" : "Create Kitchen"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
